import H from '@here/maps-api-for-javascript';
import { isEqual } from 'lodash';
import { colors } from 'global/variables';
import { createTourUnselectedSvg, createMarkerSvg } from '../../../../icons/marker';

const { defaultStopMarkerColor, white } = colors;

const DEFAULT_JOB_MARKER_COLOR = {
  delivery: { inner: defaultStopMarkerColor, outer: defaultStopMarkerColor, text: white },
  pickup: { inner: defaultStopMarkerColor, outer: defaultStopMarkerColor, text: white },
};

const ACTIVITY_ANCHOR = {
  DOT: { x: 6, y: 6 },
  DOT_HIGHLIGHTED: { x: 10, y: 10 },
  NUMBERED: { x: 10, y: 15 },
  SPECIAL_SQUARE: { x: 12, y: 35 },
};

function getActivityMarkerConfig(feature, highlight) {
  const props = feature.properties || {};
  const types = props.types || [];
  const jobMarkerColor = props.jobMarkerColor || DEFAULT_JOB_MARKER_COLOR;

  const typesObj = types.reduce((t, item) => {
    t[item] = (t[item] || 0) + 1;
    return t;
  }, {});

  const isPickup = !!typesObj.pickup;
  const colorToUse = isPickup ? jobMarkerColor.pickup : jobMarkerColor.delivery;
  const typesClass = isPickup ? 'tour-icon-pickup' : 'tour-icon-delivery';

  const highlightColor = {
    outer: 'yellow',
    inner:
      typesClass === 'tour-icon-pickup'
        ? jobMarkerColor.pickup.inner
        : jobMarkerColor.delivery.inner,
    text:
      typesClass === 'tour-icon-pickup' ? jobMarkerColor.pickup.text : jobMarkerColor.delivery.text,
  };

  const color = highlight ? highlightColor : colorToUse;

  const oneType = Object.keys(typesObj).length === 1;
  const isParkingOrRecharge = oneType && (typesObj.recharge || typesObj.parking);

  const activityOrder = props.activityOrder || 0;
  const stopIndex = props.stopIndex || 0;
  const showOrderNumber = highlight && activityOrder > 0;

  if (isParkingOrRecharge) {
    const svg = createMarkerSvg(
      color.inner,
      color.outer,
      27,
      40,
      '',
      false,
      typesObj,
      color.text,
      highlight,
      oneType,
      stopIndex,
    );
    return { svg, anchor: ACTIVITY_ANCHOR.SPECIAL_SQUARE };
  }

  if (showOrderNumber) {
    const svg = createMarkerSvg(
      color.inner,
      color.outer,
      20,
      30,
      String(activityOrder),
      props.highPriority || false,
      typesObj,
      '#fff',
      highlight,
      oneType,
      stopIndex,
    );
    return { svg, anchor: ACTIVITY_ANCHOR.NUMBERED };
  }

  const svg = createTourUnselectedSvg(color.inner, color.outer, highlight, true, stopIndex);
  return { svg, anchor: highlight ? ACTIVITY_ANCHOR.DOT_HIGHLIGHTED : ACTIVITY_ANCHOR.DOT };
}

export function setActivityMarkerIcon(marker, feature, highlight) {
  const { svg, anchor } = getActivityMarkerConfig(feature, highlight);
  marker.setIcon(new H.map.Icon(svg, { anchor }));
}

export function createActivityMarker(coords, feature, highlight) {
  const marker = new H.map.Marker({ lat: coords[1], lng: coords[0] }, { volatility: true });
  marker.setData(feature.properties);
  setActivityMarkerIcon(marker, feature, highlight);
  return marker;
}

export function updateActivityGroupMarkers(
  geoJson,
  group,
  translationsRef,
  map,
  attachHoverEventsFn,
  highlightStop,
  selectedTour,
  dispatch,
  displayRef,
) {
  const features = geoJson?.geo?.features || [];
  const newMarkerIds = new Set();
  const existingMarkers = new Map();

  group.getObjects().forEach((marker) => {
    existingMarkers.set(marker.id, marker);
  });

  features.forEach((feature) => {
    if (feature.geometry.type === 'Point') {
      const coords = feature.geometry.coordinates;
      const props = feature.properties || {};
      const id = props.jobId
        ? `${props.jobId}_${props.activityIndex ?? ''}`
        : `${coords[0]}_${coords[1]}_${props.routeId}_${props.stopIndex}_${props.activityIndex}_activity`;
      newMarkerIds.add(id);

      const highlight =
        highlightStop !== undefined &&
        highlightStop !== null &&
        props.stopIndex === highlightStop &&
        props.routeId === selectedTour;

      const existingMarker = existingMarkers.get(id);

      if (existingMarker) {
        const currentData = existingMarker.getData() || {};
        const prevHighlight = existingMarker._isHighlighted || false;

        const needsIconUpdate =
          currentData.routeId !== props.routeId ||
          !isEqual(currentData.jobMarkerColor, props.jobMarkerColor) ||
          !isEqual(currentData.types, props.types) ||
          currentData.highPriority !== props.highPriority ||
          currentData.stopIndex !== props.stopIndex ||
          currentData.activityOrder !== props.activityOrder ||
          prevHighlight !== highlight;

        // Popup data (e.g. parkingId, labels, skills, ...) must always be refreshed even when
        // none of the icon-affecting fields above changed, otherwise the hover popup keeps
        // showing stale data from whenever this marker was first created/last updated.
        existingMarker.setData(props);
        existingMarker._isHighlighted = highlight;
        if (needsIconUpdate) {
          setActivityMarkerIcon(existingMarker, feature, highlight);
        }
      } else {
        const marker = createActivityMarker(coords, feature, highlight);
        marker.id = id;
        marker._isHighlighted = highlight;
        attachHoverEventsFn(marker, translationsRef, map, dispatch, displayRef);
        group.addObject(marker);
      }
    }
  });

  [...group.getObjects()].forEach((marker) => {
    if (!newMarkerIds.has(marker.id)) {
      group.removeObject(marker);
    }
  });

  group.setVisibility(true);
}
